import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Spinner,
  Button,
  Form,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { FaSyncAlt, FaFileExcel } from "react-icons/fa";

const Landless = () => {
  const apiBaseUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const [districtSummary, setDistrictSummary] = useState([]);
  const [abhiyans, setAbhiyans] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [talukas, setTalukas] = useState([]);
  const [villages, setVillages] = useState([]);

  const [selected, setSelected] = useState({
    abhiyan: [],
    district: [],
    taluka: [],
    village: [],
  });

  const [currentView, setCurrentView] = useState("districtsAll");
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  /* ===============================
     DYNAMIC COLUMN NAME
  =============================== */
  const nameColumnHeader = selected.taluka.length
    ? "गाव"
    : selected.district.length
    ? "तालुका"
    : "जिल्हा";

  /* ===============================
     SAFE CHECKBOX DROPDOWN
  =============================== */
  const CheckboxDropdown = ({
    label,
    options,
    idKey,
    nameKey,
    selected,
    setSelected,
    disabled,
    dropdownKey,
  }) => {
    const safeOptions = Array.isArray(options) ? options : [];

    return (
      <Form.Group className="mb-3 position-relative">
        <Form.Label>{label}</Form.Label>

        <div
          className={`form-select ${disabled ? "disabled" : ""}`}
          style={{ cursor: disabled ? "not-allowed" : "pointer" }}
          onClick={() =>
            !disabled &&
            setOpenDropdown(openDropdown === dropdownKey ? null : dropdownKey)
          }
        >
          {selected.length === safeOptions.length && safeOptions.length > 0
            ? "All Selected"
            : selected.length > 0
            ? `${selected.length} Selected`
            : "All"}
        </div>

        {openDropdown === dropdownKey && !disabled && (
          <div
            className="position-absolute bg-white border rounded shadow-sm w-100"
            style={{
              bottom: "100%",
              marginBottom: "4px",
              maxHeight: 220,
              overflowY: "auto",
              zIndex: 10000,
            }}
          >
            <div className="px-3 py-2 border-bottom fw-bold">
              <Form.Check
                type="checkbox"
                label="Select All"
                checked={selected.length === safeOptions.length}
                onChange={(e) =>
                  setSelected(
                    e.target.checked ? safeOptions.map(o => o[idKey]) : []
                  )
                }
              />
            </div>

            {safeOptions.map(o => (
              <div key={o[idKey]} className="px-3 py-2">
                <Form.Check
                  type="checkbox"
                  label={o[nameKey]}
                  checked={selected.includes(o[idKey])}
                  onChange={() => {
                    setSelected(
                      selected.includes(o[idKey])
                        ? selected.filter(x => x !== o[idKey])
                        : [...selected, o[idKey]]
                    );

                    // ✅ CLOSE ONLY LAST DROPDOWN (VILLAGE)
                    if (dropdownKey === "village") {
                      setOpenDropdown(null);
                    }
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </Form.Group>
    );
  };

  /* ===============================
     SAFE FETCH
  =============================== */
  const fetchData = useCallback(
    async (endpoint, setter) => {
      setLoading(true);
      try {
        const res = await fetch(`${apiBaseUrl}${endpoint}`);
        const data = await res.json();
        setter(Array.isArray(data) ? data : []);
      } catch {
        setter([]);
      } finally {
        setLoading(false);
      }
    },
    [apiBaseUrl]
  );

  /* ===============================
     DATA LOAD
  =============================== */
  useEffect(() => {
    fetchData("/landless/districts", setDistrictSummary);
    fetchData("/landless/abhiyans", setAbhiyans);
  }, [fetchData]);

  useEffect(() => {
    if (selected.abhiyan.length) {
      fetchData(
        `/landless/abhiyans/${selected.abhiyan.join(",")}/districts`,
        setDistricts
      );
      setCurrentView("districts");
      setSelected(p => ({ ...p, district: [], taluka: [], village: [] }));
    }
  }, [selected.abhiyan, fetchData]);

  useEffect(() => {
    if (selected.district.length) {
      fetchData(
        `/landless/abhiyans/${selected.abhiyan.join(",")}/districts/${selected.district.join(",")}/talukas`,
        setTalukas
      );
      setCurrentView("talukas");
      setSelected(p => ({ ...p, taluka: [], village: [] }));
    }
  }, [selected.district, selected.abhiyan, fetchData]);

  useEffect(() => {
    if (selected.taluka.length) {
      fetchData(
        `/landless/abhiyans/${selected.abhiyan.join(",")}/districts/${selected.district.join(",")}/talukas/${selected.taluka.join(",")}/villages`,
        setVillages
      );
      setCurrentView("villages");
      setSelected(p => ({ ...p, village: [] }));
    }
  }, [selected.taluka, selected.district, selected.abhiyan, fetchData]);

  /* ===============================
     FILTER
  =============================== */
  const dataMap = {
    districtsAll: districtSummary,
    districts,
    talukas,
    villages,
  };

  useEffect(() => {
    const data = dataMap[currentView] || [];
    setFilteredData(
      data.filter(r =>
        Object.values(r).join(" ").toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, currentView, districtSummary, districts, talukas, villages]);

  /* ===============================
     TOTALS
  =============================== */
  const totals = filteredData.reduce(
    (a, b) => ({
      yekun_bhumihin: a.yekun_bhumihin + +b.yekun_bhumihin,
      pdu: a.pdu + +b.pdu,
      shashkiy_jaga: a.shashkiy_jaga + +b.shashkiy_jaga,
      atikraman: a.atikraman + +b.atikraman,
      etar_margane: a.etar_margane + +b.etar_margane,
      total: a.total + +b.total,
      urvarit_labharthi: a.urvarit_labharthi + +b.urvarit_labharthi,
    }),
    {
      yekun_bhumihin: 0,
      pdu: 0,
      shashkiy_jaga: 0,
      atikraman: 0,
      etar_margane: 0,
      total: 0,
      urvarit_labharthi: 0,
    }
  );

  /* -----------------------------------------------------------
   WHEN VILLAGE SELECTED → FILTER ONLY THAT VILLAGE
----------------------------------------------------------- */
useEffect(() => {
  if (selected.village.length > 0 && villages.length > 0) {
    const filtered = villages.filter(v =>
      selected.village.includes(v.village_id)
    );

    setFilteredData(Array.isArray(filtered) ? filtered : []);
  }
}, [selected.village, villages]);


  /* ===============================
     EXPORT
  =============================== */
  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Landless Report");
    XLSX.writeFile(wb, "Landless_Report.xlsx");
  };

  return (
    <Container fluid>
      <Row>
        {/* SIDEBAR */}
        <Col md={2} className="p-3">
          <Button className="w-100 mb-3" variant="secondary" onClick={() => navigate("/")}>
            Back
          </Button>

          <div className="p-3 rounded shadow-sm" style={{ backgroundColor: "#EAF0F9" }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="fw-bold mb-0" style={{ color: "#1F3C88" }}>Filters</h5>
              <FaSyncAlt style={{ cursor: "pointer" }} onClick={() => window.location.reload()} />
            </div>

            <Form>
              <CheckboxDropdown label="अभियान" options={abhiyans} idKey="abhiyan_id" nameKey="abhiyan_name"
                selected={selected.abhiyan} setSelected={v => setSelected(p => ({ ...p, abhiyan: v }))} dropdownKey="abhiyan" />

              <CheckboxDropdown label="जिल्हा" options={districts} idKey="district_id" nameKey="district"
                selected={selected.district} setSelected={v => setSelected(p => ({ ...p, district: v }))} disabled={!selected.abhiyan.length} dropdownKey="district" />

              <CheckboxDropdown label="तालुका" options={talukas} idKey="taluka_id" nameKey="taluka"
                selected={selected.taluka} setSelected={v => setSelected(p => ({ ...p, taluka: v }))} disabled={!selected.district.length} dropdownKey="taluka" />

              <CheckboxDropdown label="गाव" options={villages} idKey="village_id" nameKey="village"
                selected={selected.village} setSelected={v => setSelected(p => ({ ...p, village: v }))} disabled={!selected.taluka.length} dropdownKey="village" />
            </Form>
          </div>
        </Col>

        {/* MAIN */}
        <Col md={10} className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="fw-bold" style={{ color: "#1F3C88" }}>
              प्रधानमंत्री आवास योजना ग्रामीण मधील भूमिहीन लाभार्थी
            </h5>

            <div className="d-flex gap-2">
              <Form.Control style={{ width: 220 }} placeholder="Search" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
              <Button variant="success" onClick={exportToExcel}><FaFileExcel /> Export</Button>
            </div>
          </div>

          {loading ? <Spinner /> : (
            <div style={{ maxHeight: "500px", overflowY: "auto" }}>
            <Table bordered hover>
              <thead className="bg-light">
                <tr>
                  <th>अ.न.</th>
                  <th>{nameColumnHeader}</th>
                  <th>एकुण भूमिहीन लाभार्थी</th> 
                  <th>पंडीत दीन दयाळ उपाध्याय घरकुल जागा खरेदी अर्थ सहाय्य योजना</th> 
                  <th>शासकीय जागा विनामूल्य उपलब्ध करण्याची योजना</th> 
                  <th>अतिक्रमणे नियमानुकूल करणे योजना</th>
                   <th>इतर</th> 
                   <th>आज अखेर जागा उपलब्ध</th> 
                   <th>उर्वरित भूमिहीन लाभार्थी</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ fontWeight: "bold", background: "#eef3ff" }}>
                  <td colSpan={2}>एकूण</td>
                  <td>{totals.yekun_bhumihin}</td>
                  <td>{totals.pdu}</td>
                  <td>{totals.shashkiy_jaga}</td>
                  <td>{totals.atikraman}</td>
                  <td>{totals.etar_margane}</td>
                  <td>{totals.total}</td>
                  <td>{totals.urvarit_labharthi}</td>
                </tr>

                {filteredData.map((r, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td>{r.district || r.taluka || r.village}</td>
                    <td>{r.yekun_bhumihin}</td>
                    <td>{r.pdu}</td>
                    <td>{r.shashkiy_jaga}</td>
                    <td>{r.atikraman}</td>
                    <td>{r.etar_margane}</td>
                    <td>{r.total}</td>
                    <td>{r.urvarit_labharthi}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            </div>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default Landless;
